/* Legato ECP Portal */
(() => {
  const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

  // HOYA's order reference field takes at most 29 plain ASCII characters (DIG-981 E).
  const REFERENCE_MAX = 29;
  const REFERENCE_PATTERN = /^[\x20-\x7E]*$/;

  async function updateCartCount() {
    const response = await fetch(`${root}cart.js`, { headers: { Accept: 'application/json' } });
    if (!response.ok) return;
    const cart = await response.json();
    document.querySelectorAll('[data-cart-count]').forEach((badge) => {
      badge.textContent = cart.item_count;
      badge.hidden = cart.item_count === 0;
    });
    document.querySelectorAll('[data-cart-shown]').forEach((element) => {
      element.hidden = cart.item_count === 0;
    });
    document.querySelectorAll('[data-cart-link]').forEach((link) => {
      link.setAttribute('aria-label', `Cart, ${cart.item_count} ${cart.item_count === 1 ? 'frame' : 'frames'}`);
    });
  }

  /* Frame photos: hover or keyboard focus is CSS; a tap toggles the angled view. */
  function initFrameMedia() {
    const touchOnly = window.matchMedia('(hover: none)');
    document.querySelectorAll('[data-frame-media]').forEach((media) => {
      media.addEventListener('click', () => {
        if (touchOnly.matches) media.classList.toggle('is-angled');
      });
      media.addEventListener('keydown', (event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          media.classList.toggle('is-angled');
        }
      });
    });
  }

  /* "Show detailed specs" opens the specs on every card so frames can be compared. */
  function initSpecs() {
    const toggles = document.querySelectorAll('[data-specs-toggle]');
    toggles.forEach((toggle) => {
      toggle.addEventListener('click', () => {
        const open = toggle.getAttribute('aria-expanded') !== 'true';
        toggles.forEach((other) => {
          other.setAttribute('aria-expanded', String(open));
          other.querySelector('[data-specs-label]').textContent = open ? 'Hide detailed specs' : 'Show detailed specs';
        });
        document.querySelectorAll('[data-specs]').forEach((list) => {
          list.hidden = !open;
        });
      });
    });
  }

  /* Reference pop-up: one frame per confirmed Add to cart, each its own cart line. */
  function initReferenceDialog() {
    const dialog = document.querySelector('[data-ref-dialog]');
    if (!dialog) return;

    const form = dialog.querySelector('[data-ref-form]');
    const input = dialog.querySelector('[data-ref-input]');
    const error = dialog.querySelector('[data-ref-error]');
    const submit = dialog.querySelector('[data-ref-submit]');
    const toast = document.querySelector('[data-toast]');
    let frame = null;
    let opener = null;
    let toastTimer = null;

    const setError = (message) => {
      error.textContent = message;
      input.setAttribute('aria-invalid', message ? 'true' : 'false');
    };

    const showToast = (message) => {
      if (!toast) return;
      toast.querySelector('[data-toast-text]').textContent = message;
      toast.hidden = false;
      clearTimeout(toastTimer);
      toastTimer = setTimeout(() => {
        toast.hidden = true;
      }, 6000);
    };

    document.querySelectorAll('[data-add-frame]').forEach((button) => {
      button.addEventListener('click', () => {
        opener = button;
        frame = button.dataset;
        dialog.querySelector('[data-ref-name]').textContent = frame.name;
        dialog.querySelector('[data-ref-detail]').textContent = frame.detail;
        dialog.querySelector('[data-ref-price]').textContent = frame.price;
        const image = dialog.querySelector('[data-ref-image]');
        image.src = frame.image || '';
        image.alt = `${frame.name}, front view`;
        image.hidden = !frame.image;
        input.value = '';
        setError('');
        submit.disabled = false;
        dialog.showModal();
        input.focus();
      });
    });

    dialog.querySelector('[data-ref-close]').addEventListener('click', () => dialog.close());
    dialog.addEventListener('click', (event) => {
      if (event.target === dialog) dialog.close();
    });
    dialog.addEventListener('close', () => {
      if (opener) opener.focus();
    });

    input.addEventListener('input', () => {
      if (error.textContent) setError(validate(input.value));
    });

    function validate(value) {
      if (value.length > REFERENCE_MAX) return `Use ${REFERENCE_MAX} characters or fewer.`;
      if (!REFERENCE_PATTERN.test(value)) {
        return 'Use plain letters, numbers, spaces and simple punctuation only (no accents or symbols like é or €).';
      }
      return '';
    }

    /*
      LensAdvisor adds the frame, not us: its Quick Buy flow (Frame Only) puts
      the frame in the cart and registers it with LensAdvisor, which is what
      gets the order tagged and sent to HOYA. See docs/lensadvisor-hoya.md.
    */
    let pending = null;
    let revealTimer = null;

    /*
      With "skip if only one prescription type" on, LensAdvisor picks Frame Only
      and adds the frame without showing its own modal, so the modal stays
      hidden (html.is-adding-frame) while it works. If it shows its selection
      step anyway, or errors, or takes too long, reveal it so the buyer can
      carry on there.
    */
    const revealLensAdvisor = () => {
      clearTimeout(revealTimer);
      document.documentElement.classList.remove('is-adding-frame');
    };
    ['LensAdvizor:prescriptionTypes:rendered', 'LensAdvizor:action:exception', 'LensAdvizor:addToCart:error'].forEach(
      (name) => document.addEventListener(name, revealLensAdvisor)
    );

    const onLensAdvisorAdded = async () => {
      revealLensAdvisor();
      await updateCartCount();
      if (!pending) return;
      showToast(pending.reference ? `${pending.name} for ${pending.reference} is in your cart.` : `${pending.name} is in your cart.`);
      pending = null;
    };
    // Used when LensAdvisor's "Add to Cart Custom Event" setting is on;
    // otherwise LensAdvisor goes to the cart page after adding.
    const hookLensAdvisor = () => {
      if (window.LensAdvizor) window.LensAdvizor.postAddToCart = onLensAdvisorAdded;
    };
    hookLensAdvisor();
    document.addEventListener('LensAdvizor:init:complete', hookLensAdvisor);
    document.addEventListener('LensAdvizor:init-quick-buy:complete', hookLensAdvisor);

    form.addEventListener('submit', (event) => {
      event.preventDefault();
      const reference = input.value.trim();
      const message = validate(reference);
      if (message) {
        setError(message);
        input.focus();
        return;
      }

      const quickBuy = document.querySelector(`[data-lensadvisor-variant="${frame.variantId}"]`);
      if (!window.LensAdvizor || !quickBuy) {
        setError('Ordering isn’t available right now. Reload the page and try again.');
        return;
      }

      // Set on every add, so one frame's reference never carries over to the next.
      window.LensAdvizor.customBaseProductProperties = reference ? { Reference: reference } : {};
      pending = { name: frame.name, reference };
      dialog.close();
      showToast(`Adding ${frame.name}…`);
      document.documentElement.classList.add('is-adding-frame');
      revealTimer = setTimeout(revealLensAdvisor, 10000);
      quickBuy.click();
    });
  }

  /* Lab picker in the practice bar: close on Escape or a click outside. */
  function initLabPicker() {
    const picker = document.querySelector('[data-lab-picker]');
    if (!picker) return;
    document.addEventListener('click', (event) => {
      // The cart's "Change lab" link opens the picker, so its click mustn't close it again.
      if (event.target.closest('[data-open-lab-picker]')) return;
      if (picker.open && !picker.contains(event.target)) picker.open = false;
    });
    picker.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && picker.open) {
        picker.open = false;
        picker.querySelector('summary').focus();
      }
    });
    document.querySelectorAll('[data-open-lab-picker]').forEach((link) => {
      link.addEventListener('click', (event) => {
        event.preventDefault();
        picker.open = true;
        window.scrollTo({ top: 0, behavior: 'smooth' });
        const current = picker.querySelector('a[aria-current="true"]') || picker.querySelector('a');
        if (current) current.focus({ preventScroll: true });
      });
    });
  }

  /* Selling location: saved to the cart as soon as it's chosen; checkout waits for it. */
  function initSellingLocation() {
    const select = document.querySelector('[data-selling-location]');
    const checkout = document.querySelector('[data-checkout]');
    if (!select || !checkout) return;
    const hubspotId = document.querySelector('[data-selling-location-id]');

    const sync = () => {
      const option = select.selectedOptions[0];
      hubspotId.value = (option && option.dataset.hubspotId) || '';
      checkout.disabled = !select.value;
    };
    sync();

    select.addEventListener('change', () => {
      sync();
      fetch(`${root}cart/update.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          attributes: { 'Selling location': select.value, _selling_location_hubspot_id: hubspotId.value },
        }),
      });
    });
  }

  /*
    Right after sign-in, a Flow is still linking the buyer to their practice by
    email domain. Wait and reload a few times before admitting defeat.
  */
  const LINK_ATTEMPTS = 8;
  const LINK_INTERVAL = 3000;

  function initGateLinking() {
    const url = new URL(window.location.href);
    if (!url.searchParams.has('signed_in')) return;

    const linking = document.querySelector('[data-gate-linking]');
    if (!linking) {
      // Signed in and through the gate: tidy the URL.
      url.searchParams.delete('signed_in');
      url.searchParams.delete('linking');
      window.history.replaceState(null, '', url);
      return;
    }

    const attempt = Number(url.searchParams.get('linking')) || 0;
    if (attempt >= LINK_ATTEMPTS) return;
    linking.hidden = false;
    document.querySelector('[data-gate-not-found]').hidden = true;
    setTimeout(() => {
      url.searchParams.set('linking', attempt + 1);
      window.location.replace(url);
    }, LINK_INTERVAL);
  }

  document.addEventListener('DOMContentLoaded', () => {
    initGateLinking();
    initFrameMedia();
    initSpecs();
    initReferenceDialog();
    initLabPicker();
    initSellingLocation();
  });
})();
